/**
 * DataGridCellNumber — Number input cell variant
 *
 * Renders a text input with inputMode="decimal" for numeric entry.
 * Validates on commit — rejects non-numeric input and reverts.
 * Auto-focuses and selects all text on mount.
 */

import { useRef, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { DataGridCellVariantProps } from './types';

export function DataGridCellNumber({
  value,
  onCommit,
  onCancel,
  onStartEditing,
  saveState,
}: DataGridCellVariantProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(value != null ? String(value) : '');
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
      onStartEditing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCommit = () => {
    const trimmed = localValue.trim();
    if (trimmed === '' && (value === null || value === undefined || value === 0)) {
      // No change
      onCancel();
      return;
    }
    if (trimmed === '') {
      onCommit(0);
      return;
    }
    const num = Number(trimmed);
    if (isNaN(num)) {
      // Invalid: flash red, revert
      setInvalid(true);
      setTimeout(() => setInvalid(false), 300);
      onCancel();
      return;
    }
    onCommit(num);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
    } else if (e.key === 'Enter') {
      e.stopPropagation();
      handleCommit();
    } else if (e.key === 'Tab') {
      handleCommit();
    } else {
      e.stopPropagation();
    }
  };

  return (
    <div className="relative flex items-center w-full h-full">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
          setInvalid(false);
        }}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
        className={`w-full h-full bg-transparent text-sm outline-none border-none px-1 text-right ${
          invalid ? 'animate-shake text-red-500' : ''
        }`}
        aria-label="Edit number"
      />
      {saveState.status === 'saving' && (
        <Loader2 className="absolute top-0.5 right-0.5 w-3 h-3 text-muted-foreground animate-spin" />
      )}
    </div>
  );
}
